import { useEffect, useMemo, useRef } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { LoadingIndicator } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type QuickSelectOption } from "@features/metrics-explorer/types";

import { Chart } from "./chart";
import { ChartControls } from "./chart-controls";
import { ChartLegend } from "./chart-legend";
import { EmptyState } from "./empty-state";
import { Header } from "./header";
import { MetricCards } from "./metric-cards";

interface Props {
  onAddTile: () => void;
  onQuickSelect: (value: QuickSelectOption) => void;
}

export const MetricsContent = ({ onAddTile, onQuickSelect }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    metricsData,
    indexData,
    tiles,
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      filterQuery,
      timeGranularity,
      dateRange,
    },
    setTiles,
    setPdfExportAreaRef,
  } = useMetricsExplorerContext();
  const { addToast } = useToasts();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    filterQuery,
    dateRange,
  };

  const noticeDisplayed = useRef(false);

  useEffect(() => {
    const activeTiles = tiles.filter((e) => e.active);

    if (activeTiles.length > 3 && !noticeDisplayed.current) {
      noticeDisplayed.current = true;
      addToast(
        "Axes has been removed because you've chosen more than 3 metrics",
        {
          appearance: "info",
          autoDismiss: true,
          id: "metrics-explorer-axes-removed-toast",
          autoDismissTimeout: 3000,
        },
      );
    }

    if (activeTiles.length <= 3) {
      noticeDisplayed.current = false;
    }
  }, [tiles]);

  const isLoading = useMemo(() => {
    const isMetricsDataLoading = metricsData.some(
      (e) => e.status === "loading",
    );
    const isIndexDataLoading = indexData.some((e) => e.status === "loading");

    return isMetricsDataLoading || isIndexDataLoading;
  }, [metricsData, indexData]);

  return (
    <div ref={setPdfExportAreaRef} className="px-6 py-4 bg-v2-primary">
      <div className="mb-5">
        <Header />
      </div>
      <MetricCards onAddTile={onAddTile} onQuickSelect={onQuickSelect} />
      {metricsData.length > 0 && (
        <div className="mb-4 border border-v2-tertiary rounded-lg px-4 py-4">
          <div className="mb-2">
            <ChartControls />
          </div>
          <div className="w-full h-[400px] mb-3 relative">
            {isLoading && (
              <div
                className={clsx(
                  "absolute left-[50%] top-[50%] translate-x-[-26px] translate-y-[-26px]",
                )}
              >
                <LoadingIndicator />
              </div>
            )}
            <Chart />
          </div>
          <ChartLegend
            tiles={tiles}
            onChange={(id) => {
              const newTiles = tiles.map((e) =>
                e.id === id ? { ...e, visible: !e.visible } : e,
              );

              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onClick",
                additionalActionInfo: "Change tile visibility",
                data: {
                  newTiles,
                  pageContext,
                },
              });

              setTiles(newTiles);
            }}
          />
        </div>
      )}
      {metricsData.length === 0 && (
        <EmptyState onAddTile={onAddTile} onQuickSelect={onQuickSelect} />
      )}
    </div>
  );
};
